import { CodePanel } from "@/components/guide/code-panel";
import { Keycaps } from "@/components/guide/explainers";
import type { Chapter } from "@/components/guide/tutorial";
import { Screenshot } from "@/components/screenshot";

/**
 * Chapter 2: a first drive, by hand, before any code -- to see what the agent will see and to
 * learn what a check-in is. The previews are what the terminal actually prints, the keys, and
 * the run's page.
 */

const TRANSCRIPT = `$ uv run source/keyboard_agent.py
✓ checking your agent on a sample frame
Start attempt 1 of 3 on Practice maze? The attempt is spent once your code connects. [Y/n]`;

export const driveChapter: Chapter = {
  id: "drive",
  label: "Drive",
  title: "Drive yourself",
  intro: "Drive the robot by hand first. You will see what your agent will see, and what checking in means. This uses one practice attempt.",
  steps: [
    {
      title: "Start a session",
      body: (
        <>
          A run is a session on the course server: the robot is placed at the start and your
          program is connected to it. The script tells you how many attempts you have left and asks
          before starting one. An attempt is used the moment the connection opens. A window opens,
          and so does the run&apos;s live page on this site.
        </>
      ),
    },
    {
      title: "Look, then move",
      body: (
        <>
          The window shows exactly what the agent gets: the camera frame on the left, the four goal
          photos and the step count on the right. No map, no position. Each arrow press is one
          tick, and a run has a fixed budget of ticks. Look for the wall textures that appear in
          the goal photos and drive towards them.
        </>
      ),
    },
    {
      title: "Check in",
      body: (
        <>
          Checking in means telling the server &quot;I am at the goal&quot;. Press space when you
          think you are. The run is scored at that exact position and ends. Press esc to quit
          without a score. The run&apos;s page then shows your score, distance from the goal, heading
          error, and the path you drove. Missing on the first try is normal.
        </>
      ),
    },
  ],
  preview: (step) =>
    [
      <CodePanel key="run" chunks={[{ from: 0, code: TRANSCRIPT }]} step={0} language="text" title="Terminal" />,
      <Keycaps key="keys" />,
      <Screenshot
        key="run-page"
        src="/getting-started/run-page.webp"
        alt="Screenshot: the run's page, with the score, distance from the goal, heading error and the path driven"
        className="w-full"
      />,
    ][step],
};
